import { useState, useEffect, useRef } from 'react';
import MenuWebSocketClient from '../services/MenuWebSocketService';

/**
 * Hook personalizado para manejar conexión WebSocket del menú de TV
 * Proporciona actualizaciones en tiempo real para pantallas de TV
 */
const useMenuWebSocket = (shopBranchId) => {
    const [flavorCategories, setFlavorCategories] = useState([]);
    const [flavors, setFlavors] = useState([]);
    const [iceCreamProducts, setIceCreamProducts] = useState([]);
    const [isConnected, setIsConnected] = useState(false);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState(null);
    const [reconnectAttempts, setReconnectAttempts] = useState(0);
    
    const clientRef = useRef(null);

    useEffect(() => {
        if (!shopBranchId) {
            setError('shopBranchId es requerido');
            setIsLoading(false);
            return;
        }

        // Crear cliente WebSocket
        const client = new MenuWebSocketClient(shopBranchId);
        clientRef.current = client;

        // Configurar callbacks para actualizaciones
        client.onFlavorCategoriesUpdate = (categories) => {
            console.log('🔄 Actualizando categorías de gustos:', categories.length);
            setFlavorCategories(categories);
            setIsLoading(false);
        };

        client.onFlavorsUpdate = (flavorsData) => {
            console.log('🔄 Actualizando gustos:', flavorsData.length);
            setFlavors(flavorsData);
            setIsLoading(false);
        };

        client.onIceCreamProductsUpdate = (products) => {
            console.log('🔄 Actualizando productos de helados:', products.length);
            setIceCreamProducts(products);
            setIsLoading(false);
        };

        client.onCompleteMenuUpdate = (completeMenu) => {
            console.log('🔄 Actualizando menú completo:', completeMenu);
            setFlavorCategories(completeMenu.flavorCategories || []);
            setFlavors(completeMenu.flavors || []);
            setIceCreamProducts(completeMenu.iceCreamProducts || []);
            setIsLoading(false);
        };

        client.onConnectionChange = (connected) => {
            console.log('🔄 Estado de conexión:', connected ? 'Conectado' : 'Desconectado');
            setIsConnected(connected);
            if (connected) {
                setError(null);
                setReconnectAttempts(0);
            }
        };

        client.onError = (err) => {
            console.error('❌ Error WebSocket:', err);
            setError(err.message || 'Error de conexión WebSocket');
            setIsLoading(false);
            
            // Actualizar intentos de reconexión
            const status = client.getConnectionStatus();
            setReconnectAttempts(status.reconnectAttempts);
        };

        // Conectar
        const connectClient = async () => {
            try {
                setIsLoading(true);
                setError(null);
                await client.connect();
            } catch (err) {
                console.error('❌ Error conectando WebSocket:', err);
                setError(err.message || 'Error de conexión');
                setIsLoading(false);
            }
        };

        connectClient();

        // Cleanup al desmontar el componente
        return () => {
            console.log('🧹 Limpiando conexión WebSocket');
            if (clientRef.current) {
                clientRef.current.disconnect();
            }
        };
    }, [shopBranchId]);

    // Métodos para solicitar datos específicos
    const requestFlavorCategories = () => {
        if (clientRef.current) {
            clientRef.current.requestFlavorCategories();
        }
    };

    const requestFlavors = () => {
        if (clientRef.current) {
            clientRef.current.requestFlavors();
        }
    };

    const requestFlavorsByCategory = (categoryId) => {
        if (clientRef.current) {
            clientRef.current.requestFlavorsByCategory(categoryId);
        }
    };

    const requestIceCreamProducts = () => {
        if (clientRef.current) {
            clientRef.current.requestIceCreamProducts();
        }
    };

    // Método para reconectar manualmente
    const reconnect = async () => {
        if (clientRef.current) {
            try {
                setIsLoading(true);
                setError(null);
                await clientRef.current.connect();
            } catch (err) {
                setError(err.message || 'Error de reconexión');
                setIsLoading(false);
            }
        }
    };

    // Obtener estado de conexión detallado
    const getConnectionInfo = () => {
        if (clientRef.current) {
            return clientRef.current.getConnectionStatus();
        }
        return { isConnected: false, reconnectAttempts: 0, maxReconnectAttempts: 5 };
    };

    return {
        // Datos del menú
        flavorCategories,
        flavors,
        iceCreamProducts,
        
        // Estados de conexión
        isConnected,
        isLoading,
        error,
        reconnectAttempts,
        
        // Métodos para solicitar datos
        requestFlavorCategories,
        requestFlavors,
        requestFlavorsByCategory,
        requestIceCreamProducts,
        
        // Utilidades
        reconnect,
        getConnectionInfo
    };
};

export default useMenuWebSocket;